import { fromDateTimeLocal } from "../../components/utils/utility";

const API_URL = process.env.REACT_APP_API_URL;

const buildFilterParams = (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.studentId) params.set("studentId", filters.studentId);
  if (filters.subjectId) params.set("subjectId", filters.subjectId);
  if (filters.batchId) params.set("batchId", filters.batchId);
  if (filters.result) params.set("result", filters.result);
  // Which kind of question: a section type, "test", or "test:<testId>".
  if (filters.responseType) params.set("responseType", filters.responseType);
  // The pickers hand back a zone-less local wall clock; the server does
  // new Date(...) in UTC, so these must be converted or the window is shifted
  // by the admin's own offset.
  if (filters.fromDate) params.set("fromDate", fromDateTimeLocal(filters.fromDate));
  if (filters.toDate) params.set("toDate", fromDateTimeLocal(filters.toDate));
  return params;
};

export const fetchStudentResponses = async (page = 1, limit = 10, filters = {}) => {
  const params = buildFilterParams(filters);
  params.set("page", page);
  params.set("limit", limit);

  const res = await fetch(`${API_URL}/api/student-responses?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to fetch student responses");
  const json = await res.json();
  return json.data; // { responses, total }
};

export const fetchStudentResponseFilterOptions = async () => {
  const res = await fetch(`${API_URL}/api/filter`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filterscreen: "studentResponses" }),
  });
  if (!res.ok) throw new Error("Failed to fetch filter options");
  return await res.json(); // { students, subjects, batches }
};

export const reevaluateResponse = async (id) => {
  const res = await fetch(`${API_URL}/api/student-responses/${id}/reevaluate`, {
    method: "POST",
  });
  if (!res.ok) {
    // e.g. "This verdict was set by hand…" — worth showing as it is.
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to re-evaluate response");
  }
  const json = await res.json();
  return json.data; // { _id, correct, correctAnswerText }
};

export const reevaluateAllResponses = async (filters = {}) => {
  const params = buildFilterParams(filters);
  const res = await fetch(`${API_URL}/api/student-responses/reevaluate-all?${params.toString()}`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to re-evaluate responses");
  const json = await res.json();
  return json.data; // { totalMatched, totalChanged }
};

export const markResponseCorrect = async (id) => {
  const res = await fetch(`${API_URL}/api/student-responses/${id}/mark-correct`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to mark response correct");
  }
  const json = await res.json();
  return json.data; // { _id, correct, correctAnswerText }
};

// Change verdict: "correct" | "incorrect", on any answered response.
export const setResponseVerdict = async (id, verdict) => {
  const res = await fetch(`${API_URL}/api/student-responses/${id}/verdict`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ verdict }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || "Failed to change the verdict");
  }
  const json = await res.json();
  return json.data; // { _id, correct, reviewStatus, reviewedAt, reviewedByName }
};

// Headline figures for the current filter selection. Built from the same
// pipeline as the list, so the two can never disagree.
export const fetchStudentResponsesSummary = async (filters = {}) => {
  const params = buildFilterParams(filters);
  const res = await fetch(`${API_URL}/api/student-responses/summary?${params.toString()}`);
  if (!res.ok) throw new Error("Failed to fetch summary");
  const json = await res.json();
  return json.data;
};
